import { clampLevel, elementAddress, fixtureIntensityKeys, type PanelModel } from './panelElements';
import type { FixtureDescriptor, RigSnapshot, SceneSummary } from './types';

/**
 * One fixture's share of a saved scene, exactly as the record carries it.
 *
 * `channels` holds BOTH scopes in one bag. A bare key (`intensity/dimmer`) is the whole-device value;
 * a key prefixed `@<index>/` (`@7/intensity/dimmer`) belongs to that one element of the device. The same
 * record may ALSO spell the per-element values out under `elements`, and the two forms mean the same
 * thing — see {@link decodeSceneLevels}.
 */
export interface SceneFixture {
  fixtureId: string;
  channels?: Record<string, number>;
  color?: Record<string, unknown>;
  elements?: Record<string, { channels?: Record<string, number> }>;
}

/** A saved scene, whole: the record the controller stores and recalls, not a list-row summary of it. */
export interface SceneRecord {
  sceneId: string;
  name: string;
  scope?: string;
  favorite?: boolean;
  revision?: number;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  fixtures?: SceneFixture[];
}

export type DecodedSceneLevels =
  | { kind: 'valid'; levels: Map<string, number> }
  | { kind: 'invalid' };

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function asChannels(value: unknown): Record<string, number> | undefined {
  const source = asRecord(value);
  if (!source) return undefined;
  const channels: Record<string, number> = {};
  for (const [key, raw] of Object.entries(source)) {
    if (typeof raw === 'number' && Number.isFinite(raw)) channels[key] = raw;
  }
  return channels;
}

function parseSceneFixture(value: unknown): SceneFixture | undefined {
  const source = asRecord(value);
  const fixtureId = typeof source?.fixtureId === 'string' ? source.fixtureId.trim() : '';
  if (!source || !fixtureId) return undefined;
  const elements: SceneFixture['elements'] = {};
  for (const [index, raw] of Object.entries(asRecord(source.elements) ?? {})) {
    const channels = asChannels(asRecord(raw)?.channels);
    if (channels) elements[index] = { channels };
  }
  const channels = asChannels(source.channels);
  const color = asRecord(source.color);
  return {
    fixtureId,
    ...(channels ? { channels } : {}),
    ...(color ? { color } : {}),
    ...(Object.keys(elements).length ? { elements } : {}),
  };
}

/**
 * Whole scene records out of untrusted remote rows — a Firestore collection, or a controller's answer.
 *
 * Tolerant on purpose: a row this console cannot read is DROPPED rather than allowed to abort the
 * library, because one malformed document must not cost an operator every other scene in the room.
 * A row needs an id and a name to be a scene at all; everything past that is optional and is kept only
 * when it is the type the schema states.
 */
export function parseSceneRecords(raw: readonly unknown[]): SceneRecord[] {
  const records: SceneRecord[] = [];
  for (const entry of raw) {
    const source = asRecord(entry);
    if (!source) continue;
    const sceneId = typeof source.sceneId === 'string' ? source.sceneId.trim() : '';
    const name = typeof source.name === 'string' ? source.name.trim() : '';
    if (!sceneId || !name) continue;
    const fixtures = Array.isArray(source.fixtures)
      ? source.fixtures.map(parseSceneFixture).filter((fixture): fixture is SceneFixture => Boolean(fixture))
      : undefined;
    const scope = asRecord(source.scope)?.kind ?? source.scope;
    records.push({
      sceneId,
      name,
      ...(typeof scope === 'string' && scope.trim() ? { scope: scope.trim() } : {}),
      ...(typeof source.favorite === 'boolean' ? { favorite: source.favorite } : {}),
      ...(typeof source.revision === 'number' && Number.isInteger(source.revision) ? { revision: source.revision } : {}),
      ...(typeof source.updatedBy === 'string' ? { updatedBy: source.updatedBy } : {}),
      ...(typeof source.createdAt === 'string' ? { createdAt: source.createdAt } : {}),
      ...(typeof source.updatedAt === 'string' ? { updatedAt: source.updatedAt } : {}),
      ...(fixtures ? { fixtures } : {}),
    });
  }
  return records;
}

/** The library row for a whole record, so one read path feeds both the cards and the drawing. */
export function sceneSummaryOf(record: SceneRecord): SceneSummary {
  return {
    sceneId: record.sceneId,
    name: record.name,
    ...(record.scope ? { scope: record.scope } : {}),
    ...(record.fixtures ? { fixtureCount: record.fixtures.length } : {}),
    ...(typeof record.favorite === 'boolean' ? { favorite: record.favorite } : {}),
  };
}

const ELEMENT_SCOPED = /^@(\d+)\/(.+)$/;

/** The first dimmer-class value this bag states, in the descriptor's own channel order, else undefined. */
function dimmerValue(channels: Record<string, number> | undefined, keys: readonly string[]): number | undefined {
  if (!channels) return undefined;
  for (const key of keys) {
    const value = channels[key];
    if (value != null) return clampLevel(value);
  }
  return undefined;
}

/**
 * A saved scene, painted onto the room's elements — the ONE decode path in the console.
 *
 * There is exactly one of these, and both read paths go through it: the scene records that arrive on the
 * Firestore listener and the whole record a controller answers `get_scene` with. A second decoder would
 * drift from this one, and the moment it did, a scene saved on one console would preview differently on
 * another while the wire showed a third thing. The preview is only honest because it is derived from the
 * same record the controller recalls.
 *
 * The precedence it reproduces is `rigruntime.ResolveChannel`'s, which is what recall actually does:
 *
 * 1. the element's own value, spelled either `@<index>/<group>/<subType>` in `channels` or
 *    `elements["<index>"].channels["<group>/<subType>"]` — the two forms are equivalent, and whichever
 *    the record carries wins for that element;
 * 2. else the whole-device value under the bare key;
 * 3. else zero — a scene is a PARTIAL look, and an element it never mentions is not lit by it.
 *
 * Which keys count as dimmer-class is the rig descriptor's answer (`fixtureIntensityKeys`), never a list
 * repeated here: the room's tape wires two parallel dimmer channels per element and both are read the
 * same way the live feed reads them.
 *
 * Returns `invalid` rather than a half-painted map: a scene with no fixtures, or a room this console has
 * not resolved yet, has nothing truthful to draw and the caller must fall back rather than show a dark
 * room that claims to be the scene.
 */
export function decodeSceneLevels(
  scene: SceneRecord | undefined,
  model: PanelModel | undefined,
  rig: RigSnapshot | undefined,
): DecodedSceneLevels {
  if (!scene?.fixtures?.length || !model?.elements.length || !rig) return { kind: 'invalid' };
  const descriptors = new Map<string, FixtureDescriptor>(rig.fixtures.map((fixture) => [fixture.deviceId, fixture]));

  const wholeDevice = new Map<string, number>();
  const perElement = new Map<string, number>();
  for (const fixture of scene.fixtures) {
    const keys = fixtureIntensityKeys(descriptors.get(fixture.fixtureId));
    if (!keys.length) continue;
    const bare: Record<string, number> = {};
    const scoped = new Map<string, Record<string, number>>();
    for (const [key, value] of Object.entries(fixture.channels ?? {})) {
      const match = ELEMENT_SCOPED.exec(key);
      if (!match) bare[key] = value;
      else scoped.set(match[1], { ...(scoped.get(match[1]) ?? {}), [match[2]]: value });
    }
    for (const [index, element] of Object.entries(fixture.elements ?? {})) {
      scoped.set(index, { ...(scoped.get(index) ?? {}), ...(element.channels ?? {}) });
    }
    const device = dimmerValue(bare, keys);
    if (device != null) wholeDevice.set(fixture.fixtureId, device);
    for (const [index, channels] of scoped) {
      const value = dimmerValue(channels, keys);
      if (value != null) perElement.set(elementAddress(fixture.fixtureId, Number(index)), value);
    }
  }

  // Every element of the room gets an entry, including the ones this scene never mentions: the drawing
  // consumes the same vocabulary `authoritativeLevels` produces, and a missing key would read as "unknown"
  // where the scene's own answer is "dark".
  const levels = new Map<string, number>();
  for (const element of model.elements) {
    const { deviceId } = element;
    levels.set(element.key, deviceId
      ? perElement.get(elementAddress(deviceId, element.elementIndex)) ?? wholeDevice.get(deviceId) ?? 0
      : 0);
  }
  return { kind: 'valid', levels };
}
